import * as React from 'react';

import type { AssignmentRole } from 'bundles/assignment-common/types/AssignmentRoles';
import type {
  AnyData,
  Response as CompoundAssessmentsFormResponseType,
  FormElement,
  ResponseId,
  ResponseMetadata,
  Responses,
  ReviewPartSchema,
  SubmissionPartSchema,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
// TODO: unify dirnames and make them dash-case
// TODO: unify component names, somethink like CheckboxPart or smth.
import BoxViewDocumentAnnotation from 'bundles/compound-assessments/components/form-parts/boxViewDocumentAnnotation/BoxViewDocumentAnnotation';
import Checkbox from 'bundles/compound-assessments/components/form-parts/checkbox/Checkbox';
import CodeExpression from 'bundles/compound-assessments/components/form-parts/codeExpression/CodeExpression';
import FileUpload from 'bundles/compound-assessments/components/form-parts/fileUpload/FileUpload';
import Git from 'bundles/compound-assessments/components/form-parts/git/Git';
import type { FormPartData } from 'bundles/compound-assessments/components/form-parts/lib/getFormPartData';
import getFormPartData from 'bundles/compound-assessments/components/form-parts/lib/getFormPartData';
import MathExpression from 'bundles/compound-assessments/components/form-parts/mathExpression/MathExpression';
import Mcq from 'bundles/compound-assessments/components/form-parts/mcq/Mcq';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import MultiLineInput from 'bundles/compound-assessments/components/form-parts/multiLineInput/MultiLineInput';
import MultipleFillableBlanks from 'bundles/compound-assessments/components/form-parts/multipleFillableBlanks/MultipleFillableBlanks';
import OffPlatform from 'bundles/compound-assessments/components/form-parts/offPlatform/OffPlatform';
import Options from 'bundles/compound-assessments/components/form-parts/options/Options';
import PlainText from 'bundles/compound-assessments/components/form-parts/plainText/PlainText';
import Poll from 'bundles/compound-assessments/components/form-parts/poll/Poll';
import RichText from 'bundles/compound-assessments/components/form-parts/richText/RichText';
import TextAreaBox from 'bundles/compound-assessments/components/form-parts/textAreaBox/TextAreaBox';
import TextInputBox from 'bundles/compound-assessments/components/form-parts/textInputBox/TextInputBox';
import UrlPart from 'bundles/compound-assessments/components/form-parts/url/UrlPart';
import Widget from 'bundles/compound-assessments/components/form-parts/widget/Widget';
// Separate response definitions for the same thing.
// a=1a|2a|3a
// b=1b|2b|3b
// where 1a is essentially the same as 1b, etc.
// See https://phabricator.dkandu.me/D99213#inline-851142
import ChangedResponse from 'bundles/compound-assessments/components/local-state/changed-response/ChangedResponse';
import type { Response as ChangedResponseType } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import { typeNames } from 'bundles/compound-assessments/constants';
import { OverrideFormPartChangeResponseWrapper } from 'bundles/grading';

export type ChildProps = {
  prompt?: SubmissionPartSchema | ReviewPartSchema | AnyData;
  response?: CompoundAssessmentsFormResponseType | null;
  responseId?: ResponseId;
  responses?: Responses;
  storedResponse?: CompoundAssessmentsFormResponseType | null;
  metadata?: ResponseMetadata;
  isDisabled: boolean;
  isAutoSaving: boolean;
  isReadOnly: boolean;
  onChangeResponse: () => void;
  showValidation: boolean;
  courseId?: string;
  itemId?: string;
  userId?: number;
  submissionId?: string;
  role?: AssignmentRole;
  teamId?: string;
  ariaLabelledBy?: string;
  ariaDisabled?: boolean;
  isExpanded?: boolean; // only currently used by FileUpload and BoxViewDocumentAnnotation
  enableFeedbackFocus?: boolean;
  autoFocus?: boolean;
  renderFileUploadForPreview?: boolean;
  isQuickGraderContext?: boolean;
  isGraderView?: boolean;
};

export type FormPartProps = {
  formPart: FormElement;
  isReadOnly?: boolean | null;
  isDisabled?: boolean | null;
  isAutoSaving?: boolean | null;
  localScopeId?: string | null;
  showValidation?: boolean | null;
  courseId?: string;
  itemId?: string;
  userId?: number;
  teamId?: string;
  submissionId?: string;
  role?: AssignmentRole;
  ariaLabelledBy?: string;
  ariaDisabled?: boolean;
  isExpanded?: boolean;
  /** Renders read-only child component directly instead of reading from ChangedResponse
   * in grading view */
  isGraderView?: boolean;
  enableFeedbackFocus?: boolean;
  autoFocus?: boolean;
  renderFileUploadForPreview?: boolean;
  isQuickGraderContext?: boolean;
  overrideChangeResponseAvailable?: boolean;
};

const getFormPartComponent = (formPartData: FormPartData) => {
  const { prompt, response, responses, questionType } = formPartData;

  if (questionType && prompt && (response || responses)) {
    switch (questionType) {
      case 'boxViewDocumentAnnotation':
        return BoxViewDocumentAnnotation;
      case 'checkbox':
      case 'checkboxReflect':
        return Checkbox;
      case 'codeExpression':
        return CodeExpression;
      case 'mathExpression':
        return MathExpression;
      case 'mcq':
      case 'mcqReflect':
        return Mcq;
      case 'poll':
      case 'checkboxPoll':
        return Poll;
      case 'reflect':
        return TextAreaBox;
      case 'regex':
      case 'singleNumeric':
      case 'textExactMatch':
        return TextInputBox;
      case 'widget':
        return Widget;
      case 'richText':
        return RichText;
      case 'url':
        return UrlPart;
      case 'offPlatform':
        return OffPlatform;
      case 'fileUpload':
        return FileUpload;
      case 'plainText':
        return PlainText;
      case 'multiLineInput':
        return MultiLineInput;
      case 'options':
        return Options;
      case 'git':
        return Git;
      case 'multipleFillableBlanks':
        return MultipleFillableBlanks;
      default:
        // never
        break;
    }
  }
  return undefined;
};

export const FormPart = (props: FormPartProps) => {
  const {
    formPart: element,
    isDisabled,
    isAutoSaving,
    isReadOnly: overrideReadOnly,
    localScopeId,
    showValidation,
    courseId,
    itemId,
    userId,
    submissionId,
    role,
    isExpanded,
    ariaLabelledBy,
    ariaDisabled,
    isGraderView,
    enableFeedbackFocus,
    autoFocus,
    renderFileUploadForPreview,
    isQuickGraderContext,
    overrideChangeResponseAvailable,
    teamId,
  } = props;

  if (
    element.typeName === typeNames.PROMPT_WITH_MULTIPLE_RESPONSES_ELEMENT ||
    element.typeName === typeNames.PROMPT_WITH_RESPONSE_ELEMENT ||
    element.typeName === typeNames.PROMPT_REQUIRING_RESPONSE_ELEMENT ||
    element.typeName === typeNames.GIT_REPOSITORY_PROMPT_ELEMENT
  ) {
    const formPartData = getFormPartData(element);
    const { id, prompt, response, responses, questionType, isReadOnly, metadata } = formPartData;

    const isRubricPart =
      questionType === 'options' || questionType === 'multiLineInput' || questionType === 'boxViewDocumentAnnotation';

    const Component: $TSFixMe = getFormPartComponent(formPartData);

    if (Component) {
      const childProps: ChildProps = {
        prompt,
        response,
        responses,
        responseId: id,
        // storedResponse is the response stored in the backend
        storedResponse: response,
        metadata,
        isDisabled: !!isDisabled,
        isAutoSaving: !!isAutoSaving,
        isReadOnly: !!(isReadOnly || overrideReadOnly),
        onChangeResponse: () => {},
        showValidation: !!showValidation,
        courseId,
        itemId,
        userId,
        role,
        teamId,
        submissionId,
        isExpanded,
        ariaLabelledBy,
        ariaDisabled,
        enableFeedbackFocus,
        autoFocus,
        renderFileUploadForPreview,
        isQuickGraderContext,
        isGraderView,
      };

      if (childProps.isReadOnly && (isRubricPart || isGraderView || questionType === 'git')) {
        return <Component {...childProps} />;
      } else if (id && response) {
        return (
          <ChangedResponse
            id={id}
            // Separate response definitions for the same thing.
            response={response as ChangedResponseType}
            localScopeId={localScopeId}
            shouldResetLocalResponseOnMount={!isQuickGraderContext}
          >
            {({ newResponse, handleChangeResponse }) => {
              // check whether there are any features enabled that require over riding this form part's changed response
              // if so, pass along the onChangeResponse callback into the override wrapper for feature controls
              return isQuickGraderContext && overrideChangeResponseAvailable ? (
                <OverrideFormPartChangeResponseWrapper onChangeResponse={handleChangeResponse}>
                  <Component {...childProps} response={newResponse} onChangeResponse={handleChangeResponse} />
                </OverrideFormPartChangeResponseWrapper>
              ) : (
                <Component {...childProps} response={newResponse} onChangeResponse={handleChangeResponse} />
              );
            }}
          </ChangedResponse>
        );
      }
    }
  }
  return null;
};

export default FormPart;
